import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    DateTimeInput,
    required,
    useRecordContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    ButtonGroup,
    Paper,
    Grid,
    Tooltip,
    IconButton,
    Alert,
    Snackbar,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField as MuiTextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Fab,
    Popover,
} from '@mui/material';
import {
    PowerSettingsNew as PowerIcon,
    AutoMode as AutoModeIcon,
    Info as InfoIcon,
    CalendarMonth as CalendarIcon,
    TableChart as TableIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    ArrowBackIosNew as ArrowBackIosNewIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { nl } from 'date-fns/locale';
import { differenceInMinutes, isAfter, isBefore, isEqual, subDays, addDays, format } from 'date-fns';
import moment from 'moment-timezone';
import 'moment/locale/nl'; // Importeer Nederlandse locale
import { useTranslation } from 'react-i18next';
import Planning from './Planning';
import { useApi } from '../hooks/useApi';

// Utility functies voor tijdconversie met moment.js (zoals in Planning component)
const UTC2CET = (date) => {
    return moment(date).tz('Europe/Amsterdam').toDate();
};

const CET2UTC = (date) => {
    return moment(date).tz('Europe/Amsterdam').utc().toDate();
};

// Configureer moment voor Nederlandse locale
moment.locale('nl');
moment.tz.setDefault('Europe/Amsterdam');

const ScheduleCard = () => {
    const { t } = useTranslation();
    const [schedules, setSchedules] = React.useState([]);
    const [error, setError] = React.useState(null);
    const [selectedDate, setSelectedDate] = React.useState(new Date());
    const [view, setView] = React.useState('calendar');
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [editingBlock, setEditingBlock] = React.useState(null);
    const [newBlockDialogOpen, setNewBlockDialogOpen] = React.useState(false);
    const [newBlock, setNewBlock] = React.useState(() => {
        const now = new Date();
        const startTime = new Date(now);
        // Rond huidige tijd af naar volgende kwartier
        const minutes = startTime.getMinutes();
        const roundedMinutes = Math.ceil(minutes / 15) * 15;
        startTime.setMinutes(roundedMinutes, 0, 0);
        
        const endTime = new Date(startTime.getTime() + 15 * 60 * 1000); // 15 minuten later
        
        return {
            validfrom: startTime,
            validto: endTime,
            manual: true, // Default op 'Forceer Aan'
            automatic: false
        };
    });
    const notify = useNotify();
    const [datePickerAnchor, setDatePickerAnchor] = React.useState(null);
    const { get, post, delete: del } = useApi();

    const fetchSchedules = async () => {
        try {
            // Gebruik dezelfde API call als Planning component - data rond geselecteerde datum
            // Haal data op van 1 dag voor tot 1 dag na de geselecteerde datum
            const cetStart = UTC2CET(selectedDate);
            const validfrom = format(CET2UTC(subDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
            const validto = format(CET2UTC(addDays(cetStart, 1)), "yyyy-MM-dd'T'22:00:00");
            
            console.log('Fetching schedules for selected date:', {
                selectedDate: selectedDate,
                cetStart: cetStart,
                validfrom: validfrom,
                validto: validto
            });
            
            const response = await get('/schedule', {
                params: {
                    validfrom,
                    validto
                }
            });
            
            // Converteer UTC timestamps naar CET voor weergave (zoals in Planning component)
            if (response.data?.blocks?.length > 0) {
                const convertedBlocks = response.data.blocks.map(block => ({
                    ...block,
                    validfrom: moment.utc(block.validfrom).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss'),
                    validto: moment.utc(block.validto).tz('Europe/Amsterdam').format('YYYY-MM-DDTHH:mm:ss')
                }));
                console.log('Converted blocks:', convertedBlocks.length);
                setSchedules(convertedBlocks);
            } else {
                setSchedules([]);
            }
        } catch (error) {
            console.error('Error fetching schedules:', error);
            setError(error instanceof Error ? error.message : 'Onbekende fout');
            setSchedules([]); // Reset naar lege array bij een fout
        }
    };

    React.useEffect(() => {
        fetchSchedules();
        const interval = setInterval(fetchSchedules, 30000); // 30 seconden in plaats van 5
        return () => clearInterval(interval);
    }, [selectedDate]); // Herlaad data wanneer selectedDate verandert

    const handleDelete = async (id) => {
        try {
            await del(`/schedule/${id}`);
            notify('Schema verwijderd', { type: 'success' });
            fetchSchedules();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Onbekende fout');
        }
    };

    const getSchedulesForDate = (date) => {
        if (!Array.isArray(schedules)) return [];
        
        return schedules.filter(schedule => {
            const scheduleDate = moment(schedule.validfrom).tz('Europe/Amsterdam');
            const selectedDateMoment = moment(date).tz('Europe/Amsterdam');
            return scheduleDate.format('YYYY-MM-DD') === selectedDateMoment.format('YYYY-MM-DD');
        });
    };

    // Functie om overlap te controleren - alleen voor manual overrides
    const checkOverlap = (start, end, excludeId = null) => {
        // Filter alleen manual override blokken voor overlap check
        const manualOverrides = schedules.filter(schedule => 
            schedule.manual !== null && 
            (excludeId === null || schedule.id !== excludeId)
        );
        
        return manualOverrides.some(schedule => {
            const scheduleStart = new Date(schedule.validfrom);
            const scheduleEnd = new Date(schedule.validto);
            
            // Check voor overlap: nieuwe periode overlapt met bestaande manual override
            return (start < scheduleEnd && end > scheduleStart);
        });
    };

    // Functie om een blok te bewerken
    const handleEditBlock = (block) => {
        setEditingBlock({
            ...block,
            validfrom: new Date(block.validfrom),
            validto: new Date(block.validto)
        });
        setEditDialogOpen(true);
    };

    // Functie om een nieuw blok toe te voegen
    const handleAddBlock = async () => {
        const start = newBlock.validfrom;
        const end = newBlock.validto;

        // Validatie
        if (isAfter(start, end)) {
            setError('Starttijd moet voor eindtijd liggen');
            return;
        }

        if (checkOverlap(start, end)) {
            setError('Deze tijdsperiode overlapt met een bestaand manual override');
            return;
        }

        try {
            // Splits de periode op in kwartierblokken van precies 15 minuten
            const quarterWidth = 15 * 60 * 1000; // 15 minuten in milliseconden
            const blocks = [];
            
            let currentTime = start.getTime();
            while (currentTime < end.getTime()) {
                const quarterEnd = Math.min(currentTime + quarterWidth, end.getTime());
                
                // Converteer CET naar UTC voor API call
                const utcStart = moment(currentTime).tz('Europe/Amsterdam').utc();
                const utcEnd = moment(quarterEnd).tz('Europe/Amsterdam').utc();
                
                blocks.push({
                    validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                    validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                    state: newBlock.manual
                });
                
                currentTime = quarterEnd;
            }

            // Gebruik dezelfde API structuur als Planning component
            await post('/schedule', {
                blocks: blocks,
                allowBackward: true
            });
            
            notify(`${blocks.length} kwartierblok${blocks.length !== 1 ? 'ken' : ''} toegevoegd`, { type: 'success' });
            setNewBlockDialogOpen(false);
            setNewBlock(() => {
                const now = new Date();
                const startTime = new Date(now);
                // Rond huidige tijd af naar volgende kwartier
                const minutes = startTime.getMinutes();
                const roundedMinutes = Math.ceil(minutes / 15) * 15;
                startTime.setMinutes(roundedMinutes, 0, 0);
                
                const endTime = new Date(startTime.getTime() + 15 * 60 * 1000); // 15 minuten later
                
                return {
                    validfrom: startTime,
                    validto: endTime,
                    manual: true, // Default op 'Forceer Aan'
                    automatic: false
                };
            });
            fetchSchedules();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Onbekende fout');
        }
    };

    // Functie om alle manual override periodes te analyseren (aan EN uit)
    const analyzeOnPeriods = (scheduleData) => {
        if (!scheduleData.length) return [];

        // Sorteer de blokken op starttijd zodat ze altijd chronologisch zijn
        const sortedData = [...scheduleData].sort(
            (a, b) => new Date(a.validfrom).getTime() - new Date(b.validfrom).getTime()
        );

        const periods = [];
        let currentPeriod = null;

        sortedData.forEach((block) => {
            // Check voor manual overrides (aan OF uit) en autopilot aan
            const isManualOverride = block.manual !== null;
            const isAutopilotOn = block.manual === null && block.automatic;
            
            let status = null;
            let shouldGroup = false;
            
            if (block.manual === true) {
                status = 'Forceer Aan';
                shouldGroup = true;
            } else if (block.manual === false) {
                status = 'Forceer Uit'; 
                shouldGroup = true;
            } else if (isAutopilotOn) {
                status = 'Autopilot';
                shouldGroup = true;
            }

            if (shouldGroup) {
                if (!currentPeriod) {
                    // Start nieuwe periode
                    currentPeriod = {
                        start: new Date(block.validfrom).getTime(),
                        end: new Date(block.validto).getTime(),
                        status: status
                    };
                } else if (currentPeriod.status === status) {
                    // Verleng bestaande periode met dezelfde status
                    currentPeriod.end = new Date(block.validto).getTime();
                } else {
                    // Einde van periode, start nieuwe met andere status
                    periods.push(currentPeriod);
                    currentPeriod = {
                        start: new Date(block.validfrom).getTime(),
                        end: new Date(block.validto).getTime(),
                        status: status
                    };
                }
            } else if (currentPeriod) {
                // Einde van periode (geen override en geen autopilot aan)
                periods.push(currentPeriod);
                currentPeriod = null;
            }
        });

        // Voeg laatste periode toe als die er is
        if (currentPeriod) {
            periods.push(currentPeriod);
        }

        // Bereken duur voor elke periode
        return periods.map(period => {
            const duration = differenceInMinutes(period.end, period.start);
            const hours = Math.floor(duration / 60);
            const minutes = duration % 60;
            
            return {
                ...period,
                duration,
                hours,
                minutes,
                durationText: `${hours} uur${minutes > 0 ? ` en ${minutes} minuten` : ''}`
            };
        });
    };

    const handleDateClick = (event) => {
        setDatePickerAnchor(event.currentTarget);
    };

    const handleDatePickerClose = () => {
        setDatePickerAnchor(null);
    };

    const renderCalendarView = () => (
        <Planning />
    );

    const renderTableView = () => {
        // Filter schedules voor de geselecteerde dag
        const daySchedules = schedules.filter(schedule => {
            const scheduleDate = moment(schedule.validfrom).tz('Europe/Amsterdam');
            const selectedDateMoment = moment(selectedDate).tz('Europe/Amsterdam');
            return scheduleDate.format('YYYY-MM-DD') === selectedDateMoment.format('YYYY-MM-DD');
        });
        
        // Filter alleen manual override blokken (bewerkbaar)
        const manualOverrides = daySchedules.filter(schedule => schedule.manual !== null);
        
        // Analyseer alle periodes (inclusief autopilot) voor weergave
        const allOnPeriods = analyzeOnPeriods(daySchedules);
        
        // Filter alleen manual override periodes (bewerkbaar)
        const manualOnPeriods = allOnPeriods.filter(period => period.status === 'Override actief');
        
        return (
            <Box>
                {/* Datum navigatie */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <IconButton 
                        onClick={handlePreviousDay} 
                        color="primary"
                        disabled={selectedDate.getFullYear() <= 2024}
                    >
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            mx: 2, 
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                borderRadius: 1
                            },
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            transition: 'background-color 0.2s ease'
                        }}
                        onClick={handleDateClick}
                    >
                        {moment(selectedDate).tz('Europe/Amsterdam').format('dddd D MMMM YYYY')}
                    </Typography>
                    <IconButton 
                        onClick={handleNextDay} 
                        color="primary"
                    >
                        <ArrowForwardIosIcon />
                    </IconButton>
                </Box>

                {/* Date Picker Popover */}
                <Popover
                    open={Boolean(datePickerAnchor)}
                    anchorEl={datePickerAnchor}
                    onClose={handleDatePickerClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                        <DateCalendar
                            value={selectedDate}
                            onChange={(newDate) => {
                                setSelectedDate(newDate);
                                handleDatePickerClose();
                            }}
                            sx={{ 
                                '& .MuiDateCalendar-root': {
                                    width: 'auto',
                                    height: 'auto'
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Popover>

                <Paper elevation={3} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Manual overrides voor {moment(selectedDate).tz('Europe/Amsterdam').format('DD MMMM YYYY')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Fab
                                color="primary"
                                size="small"
                                onClick={() => setNewBlockDialogOpen(true)}
                            >
                                <AddIcon />
                            </Fab>
                        </Box>
                    </Box>
                    
                    {/* Alle aan-periodes weergave (read-only voor autopilot) */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        Alle aan-periodes (autopilot + overrides)
                    </Typography>
                    <TableContainer component={Paper} sx={{ mb: 3 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>Eind</TableCell>
                                    <TableCell>Duur</TableCell>
                                    <TableCell>Aantal kwartieren</TableCell>
                                    <TableCell>Acties</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allOnPeriods.map((period, index) => {
                                    // Tel het aantal kwartieren in deze periode
                                    const quarterCount = daySchedules.filter(schedule => {
                                        const scheduleStart = new Date(schedule.validfrom).getTime();
                                        const scheduleEnd = new Date(schedule.validto).getTime();
                                        return scheduleStart >= period.start && scheduleEnd <= period.end;
                                    }).length;
                                    
                                    const isManualOverride = period.status === 'Forceer Aan' || period.status === 'Forceer Uit';
                                    
                                    return (
                                        <TableRow key={index} sx={{ 
                                            backgroundColor: period.status === 'Forceer Aan' ? 'rgba(76, 175, 80, 0.1)' : 
                                                           period.status === 'Forceer Uit' ? 'rgba(244, 67, 54, 0.1)' :
                                                           'rgba(33, 150, 243, 0.1)' 
                                        }}>
                                            <TableCell>
                                                <Typography color={
                                                    period.status === 'Forceer Aan' ? 'success.main' : 
                                                    period.status === 'Forceer Uit' ? 'error.main' :
                                                    'primary.main'
                                                }>
                                                    {period.status}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{moment(period.start).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                            <TableCell>{moment(period.end).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                            <TableCell>{period.durationText}</TableCell>
                                            <TableCell>{quarterCount} kwartier{quarterCount !== 1 ? 'en' : ''}</TableCell>
                                            <TableCell>
                                                {isManualOverride ? (
                                                    <>
                                                        <Tooltip title="Bewerk alle kwartieren in deze override periode">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditPeriod(period)}
                                                                color="primary"
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Verwijder alle kwartieren in deze override periode">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeletePeriod(period)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Autopilot (read-only)
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {/* Manual override blokken weergave - uitgecommenteerd voor nu
                    <Typography variant="h6" gutterBottom>
                        Manual override kwartierblokken
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>Eind</TableCell>
                                    <TableCell>Duur</TableCell>
                                    <TableCell>Acties</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {manualOverrides.length > 0 ? (
                                    manualOverrides.map((schedule, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Typography color={schedule.manual === true ? 'success.main' : 'error.main'}>
                                                    {schedule.manual === true ? 'Forceer Aan' : 'Forceer Uit'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{moment(schedule.validfrom).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                            <TableCell>{moment(schedule.validto).tz('Europe/Amsterdam').format('HH:mm')}</TableCell>
                                            <TableCell>15 minuten</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditBlock(schedule)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteOverride(schedule)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                Geen manual overrides voor deze dag
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    */}
                </Paper>
            </Box>
        );
    };

    // Functie om een hele periode te bewerken (alleen manual overrides)
    const handleEditPeriod = (period) => {
        // Alleen toestaan voor manual override periodes (aan of uit)
        if (period.status !== 'Forceer Aan' && period.status !== 'Forceer Uit') {
            setError('Alleen manual overrides kunnen bewerkt worden');
            return;
        }
        
        // Filter schedules voor de geselecteerde dag
        const daySchedules = schedules.filter(schedule => {
            const scheduleDate = moment(schedule.validfrom).tz('Europe/Amsterdam');
            const selectedDateMoment = moment(selectedDate).tz('Europe/Amsterdam');
            return scheduleDate.format('YYYY-MM-DD') === selectedDateMoment.format('YYYY-MM-DD');
        });
        
        // Vind alle manual override kwartieren in deze periode
        const periodQuarters = daySchedules.filter(schedule => {
            const scheduleStart = new Date(schedule.validfrom).getTime();
            const scheduleEnd = new Date(schedule.validto).getTime();
            return scheduleStart >= period.start && scheduleEnd <= period.end && schedule.manual !== null;
        });
        
        if (periodQuarters.length === 0) {
            setError('Geen manual overrides gevonden in deze periode');
            return;
        }
        
        setEditingBlock({
            ...periodQuarters[0], // Gebruik eerste kwartier als basis
            validfrom: new Date(period.start),
            validto: new Date(period.end),
            isPeriodEdit: true,
            periodQuarters: periodQuarters
        });
        setEditDialogOpen(true);
    };

    // Functie om een hele periode te verwijderen (alleen manual overrides)
    const handleDeletePeriod = async (period) => {
        // Alleen toestaan voor manual override periodes (aan of uit)
        if (period.status !== 'Forceer Aan' && period.status !== 'Forceer Uit') {
            setError('Alleen manual overrides kunnen verwijderd worden');
            return;
        }
        
        // Filter schedules voor de geselecteerde dag
        const daySchedules = schedules.filter(schedule => {
            const scheduleDate = moment(schedule.validfrom).tz('Europe/Amsterdam');
            const selectedDateMoment = moment(selectedDate).tz('Europe/Amsterdam');
            return scheduleDate.format('YYYY-MM-DD') === selectedDateMoment.format('YYYY-MM-DD');
        });
        
        // Vind alle manual override kwartieren in deze periode
        const periodQuarters = daySchedules.filter(schedule => {
            const scheduleStart = new Date(schedule.validfrom).getTime();
            const scheduleEnd = new Date(schedule.validto).getTime();
            return scheduleStart >= period.start && scheduleEnd <= period.end && schedule.manual !== null;
        });
        
        if (periodQuarters.length === 0) {
            setError('Geen manual overrides gevonden in deze periode');
            return;
        }
        
        try {
            // Verwijder alle manual overrides in de periode (zoals in Planning)
            const blocks = periodQuarters.map(quarter => {
                const utcStart = moment(quarter.validfrom).tz('Europe/Amsterdam').utc();
                const utcEnd = moment(quarter.validto).tz('Europe/Amsterdam').utc();
                
                return {
                    validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                    validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                    state: null
                };
            });

            await post('/schedule?delete=true', {
                blocks: blocks,
                allowBackward: true
            });
            
            notify(`${periodQuarters.length} manual overrides verwijderd`, { type: 'success' });
            fetchSchedules();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Onbekende fout');
        }
    };

    // Aangepaste save functie voor periode bewerking
    const handleSaveEdit = async () => {
        if (!editingBlock) return;

        const start = editingBlock.validfrom;
        const end = editingBlock.validto;

        // Validatie
        if (isAfter(start, end)) {
            setError('Starttijd moet voor eindtijd liggen');
            return;
        }

        try {
            if (editingBlock.isPeriodEdit) {
                // Bewerk een hele periode
                const periodQuarters = editingBlock.periodQuarters;
                
                // Eerst verwijder alle oude kwartieren in de periode
                if (periodQuarters.length > 0) {
                    const deleteBlocks = periodQuarters.map(quarter => {
                        const utcStart = moment(quarter.validfrom).tz('Europe/Amsterdam').utc();
                        const utcEnd = moment(quarter.validto).tz('Europe/Amsterdam').utc();
                        
                        return {
                            validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                            validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                            state: null
                        };
                    });

                    await post('/schedule?delete=true', {
                        blocks: deleteBlocks,
                        allowBackward: true
                    });
                }
                
                // Splits de nieuwe periode op in kwartierblokken van precies 15 minuten
                const quarterWidth = 15 * 60 * 1000; // 15 minuten in milliseconden
                const createBlocks = [];
                
                let currentTime = start.getTime();
                while (currentTime < end.getTime()) {
                    const quarterEnd = Math.min(currentTime + quarterWidth, end.getTime());
                    
                    const utcStart = moment(currentTime).tz('Europe/Amsterdam').utc();
                    const utcEnd = moment(quarterEnd).tz('Europe/Amsterdam').utc();
                    
                    createBlocks.push({
                        validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                        validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                        state: editingBlock.manual
                    });
                    
                    currentTime = quarterEnd;
                }

                // Voeg nieuwe kwartieren toe
                if (createBlocks.length > 0) {
                    await post('/schedule', {
                        blocks: createBlocks,
                        allowBackward: true
                    });
                }
                
                notify('Periode bijgewerkt', { type: 'success' });
            } else {
                // Bewerk een enkel blok - ook hier splitsen in kwartieren
                if (checkOverlap(start, end, editingBlock.id)) {
                    setError('Deze tijdsperiode overlapt met een bestaand manual override');
                    return;
                }

                // Splits de periode op in kwartierblokken van precies 15 minuten
                const quarterWidth = 15 * 60 * 1000; // 15 minuten in milliseconden
                const blocks = [];
                
                let currentTime = start.getTime();
                while (currentTime < end.getTime()) {
                    const quarterEnd = Math.min(currentTime + quarterWidth, end.getTime());
                    
                    const utcStart = moment(currentTime).tz('Europe/Amsterdam').utc();
                    const utcEnd = moment(quarterEnd).tz('Europe/Amsterdam').utc();
                    
                    blocks.push({
                        validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                        validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                        state: editingBlock.manual
                    });
                    
                    currentTime = quarterEnd;
                }

                // Gebruik POST endpoint zoals in Planning component
                await post('/schedule', {
                    blocks: blocks,
                    allowBackward: true
                });
                
                notify(`${blocks.length} kwartierblok${blocks.length !== 1 ? 'ken' : ''} bijgewerkt`, { type: 'success' });
            }
            
            setEditDialogOpen(false);
            setEditingBlock(null);
            fetchSchedules();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Onbekende fout');
        }
    };

    const handlePreviousDay = () => {
        const yesterday = subDays(selectedDate, 1);
        // Alleen toestaan als de datum niet voor 2024 is
        if (yesterday.getFullYear() >= 2024) {
            setSelectedDate(yesterday);
        }
    };

    const handleNextDay = () => {
        const tomorrow = addDays(selectedDate, 1);
        // Geen beperking voor toekomst (onbeperkt vooruit zoals Planning)
        setSelectedDate(tomorrow);
    };

    // Functie om een manual override te verwijderen (zoals in Planning)
    const handleDeleteOverride = async (schedule) => {
        try {
            // Verwijder de manual override (zet manual terug naar null)
            const utcStart = moment(schedule.validfrom).tz('Europe/Amsterdam').utc();
            const utcEnd = moment(schedule.validto).tz('Europe/Amsterdam').utc();

            await post('/schedule?delete=true', {
                blocks: [{
                    validfrom: utcStart.format('YYYY-MM-DDTHH:mm:ss'),
                    validto: utcEnd.format('YYYY-MM-DDTHH:mm:ss'),
                    state: null
                }],
                allowBackward: true
            });
            
            notify('Manual override verwijderd', { type: 'success' });
            fetchSchedules();
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Onbekende fout');
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={view} onChange={(e, newValue) => setView(newValue)}>
                    <Tab 
                        icon={<CalendarIcon />} 
                        label={t('schedule.planning')} 
                        value="calendar"
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<TableIcon />} 
                        label={t('schedule.table')} 
                        value="table"
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {view === 'calendar' ? renderCalendarView() : renderTableView()}

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Schema bewerken</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <DateTimePicker
                                label="Starttijd"
                                value={editingBlock?.validfrom || null}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        // Rond af naar dichtstbijzijnde kwartier
                                        const roundedValue = new Date(newValue);
                                        const minutes = roundedValue.getMinutes();
                                        const roundedMinutes = Math.round(minutes / 15) * 15;
                                        roundedValue.setMinutes(roundedMinutes, 0, 0); // Zet ook seconden en milliseconden op 0
                                        setEditingBlock(prev => ({ ...prev, validfrom: roundedValue }));
                                    }
                                }}
                                shouldDisableTime={(value, view) => {
                                    if (view === 'minutes') {
                                        const minutes = value.getMinutes();
                                        return minutes % 15 !== 0; // Alleen 0, 15, 30, 45 toestaan
                                    }
                                    return false;
                                }}
                                slotProps={{ 
                                    textField: { fullWidth: true }
                                }}
                                views={['year', 'month', 'day', 'hours', 'minutes']}
                                format="dd/MM/yyyy HH:mm"
                                minutesStep={15}
                            />
                            <DateTimePicker
                                label="Eindtijd"
                                value={editingBlock?.validto || null}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        // Rond af naar dichtstbijzijnde kwartier
                                        const roundedValue = new Date(newValue);
                                        const minutes = roundedValue.getMinutes();
                                        const roundedMinutes = Math.round(minutes / 15) * 15;
                                        roundedValue.setMinutes(roundedMinutes, 0, 0); // Zet ook seconden en milliseconden op 0
                                        setEditingBlock(prev => ({ ...prev, validto: roundedValue }));
                                    }
                                }}
                                shouldDisableTime={(value, view) => {
                                    if (view === 'minutes') {
                                        const minutes = value.getMinutes();
                                        return minutes % 15 !== 0; // Alleen 0, 15, 30, 45 toestaan
                                    }
                                    return false;
                                }}
                                slotProps={{ 
                                    textField: { fullWidth: true }
                                }}
                                views={['year', 'month', 'day', 'hours', 'minutes']}
                                format="dd/MM/yyyy HH:mm"
                                minutesStep={15}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={editingBlock?.manual === true ? 'manual_on' : 'manual_off'}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setEditingBlock(prev => ({
                                            ...prev,
                                            manual: value === 'manual_on',
                                            automatic: false // Altijd false voor manual overrides
                                        }));
                                    }}
                                >
                                    <MenuItem value="manual_on">Forceer Aan</MenuItem>
                                    <MenuItem value="manual_off">Forceer Uit</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Annuleren</Button>
                    <Button onClick={handleSaveEdit} variant="contained">Opslaan</Button>
                </DialogActions>
            </Dialog>

            {/* New Block Dialog */}
            <Dialog open={newBlockDialogOpen} onClose={() => setNewBlockDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nieuw schema blok toevoegen</DialogTitle>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <DateTimePicker
                                label="Starttijd"
                                value={newBlock.validfrom}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        // Rond af naar dichtstbijzijnde kwartier
                                        const roundedValue = new Date(newValue);
                                        const minutes = roundedValue.getMinutes();
                                        const roundedMinutes = Math.round(minutes / 15) * 15;
                                        roundedValue.setMinutes(roundedMinutes, 0, 0); // Zet ook seconden en milliseconden op 0
                                        setNewBlock(prev => ({ ...prev, validfrom: roundedValue }));
                                    }
                                }}
                                shouldDisableTime={(value, view) => {
                                    if (view === 'minutes') {
                                        const minutes = value.getMinutes();
                                        return minutes % 15 !== 0; // Alleen 0, 15, 30, 45 toestaan
                                    }
                                    return false;
                                }}
                                slotProps={{ 
                                    textField: { fullWidth: true }
                                }}
                                views={['year', 'month', 'day', 'hours', 'minutes']}
                                format="dd/MM/yyyy HH:mm"
                                minutesStep={15}
                            />
                            <DateTimePicker
                                label="Eindtijd"
                                value={newBlock.validto}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        // Rond af naar dichtstbijzijnde kwartier
                                        const roundedValue = new Date(newValue);
                                        const minutes = roundedValue.getMinutes();
                                        const roundedMinutes = Math.round(minutes / 15) * 15;
                                        roundedValue.setMinutes(roundedMinutes, 0, 0); // Zet ook seconden en milliseconden op 0
                                        setNewBlock(prev => ({ ...prev, validto: roundedValue }));
                                    }
                                }}
                                shouldDisableTime={(value, view) => {
                                    if (view === 'minutes') {
                                        const minutes = value.getMinutes();
                                        return minutes % 15 !== 0; // Alleen 0, 15, 30, 45 toestaan
                                    }
                                    return false;
                                }}
                                slotProps={{ 
                                    textField: { fullWidth: true }
                                }}
                                views={['year', 'month', 'day', 'hours', 'minutes']}
                                format="dd/MM/yyyy HH:mm"
                                minutesStep={15}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={newBlock.manual === true ? 'manual_on' : 'manual_off'}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setNewBlock(prev => ({
                                            ...prev,
                                            manual: value === 'manual_on',
                                            automatic: false // Altijd false voor manual overrides
                                        }));
                                    }}
                                >
                                    <MenuItem value="manual_on">Forceer Aan</MenuItem>
                                    <MenuItem value="manual_off">Forceer Uit</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewBlockDialogOpen(false)}>Annuleren</Button>
                    <Button onClick={handleAddBlock} variant="contained">Toevoegen</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export const ScheduleList = () => (
    <Box>
        <ScheduleCard />
    </Box>
);

export const ScheduleEdit = () => (
    <Edit>
        <SimpleForm>
            <DateTimeInput source="startTime" validate={required()} />
            <DateTimeInput source="endTime" validate={required()} />
            <TextInput source="state" validate={required()} />
            <TextInput source="description" validate={required()} />
        </SimpleForm>
    </Edit>
);

export const ScheduleCreate = () => (
    <Create>
        <SimpleForm>
            <DateTimeInput source="startTime" validate={required()} />
            <DateTimeInput source="endTime" validate={required()} />
            <TextInput source="state" validate={required()} />
            <TextInput source="description" validate={required()} />
        </SimpleForm>
    </Create>
); 